/**
 * Scrolling Announcement Bar Component
 * Handles continuous scrolling animation with customizable speed and spacing
 */

class ScrollingAnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.scrolling-announcement-bar__track');
    this.content = this.querySelector('.scrolling-announcement-bar__content');
    this.messages = this.querySelectorAll('.scrolling-announcement-bar__message:not(.scrolling-announcement-bar__message--duplicate)');
    
    // Settings from data attributes
    this.scrollSpeed = parseInt(this.dataset.scrollSpeed) || 50; // px/s
    this.messageGap = parseInt(this.dataset.gap) || 80; // px
    this.messagePadding = parseInt(this.dataset.padding) || 20; // px
    
    this.isHovered = false;
    this.animationDuration = 0;
    this.resizeObserver = null;
    
    this.init();
  }
  
  init() {
    this.applySettings();
    this.setupEventListeners();
    this.calculateAndSetAnimation();
    this.setupResizeObserver();
    
    // Handle reduced motion preference
    this.handleReducedMotion();
  }
  
  applySettings() {
    // Apply CSS custom properties for spacing
    this.style.setProperty('--announcement-message-gap', `${this.messageGap}px`);
    this.style.setProperty('--announcement-message-padding', `${this.messagePadding}px`);
    
    // Apply mobile gap (smaller for mobile)
    const mobileGap = Math.max(this.messageGap * 0.75, 30);
    this.style.setProperty('--announcement-message-gap-mobile', `${mobileGap}px`);
    
    // Apply padding to each message
    this.messages.forEach(message => {
      message.style.padding = `${this.messagePadding}px`;
    });
  }
  
  setupEventListeners() {
    // Hover events for pausing animation
    this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    
    // Focus events for accessibility
    this.addEventListener('focusin', this.handleFocusIn.bind(this));
    this.addEventListener('focusout', this.handleFocusOut.bind(this));
    
    // Mobile touch events for pause/resume
    this.setupTouchEvents();
    
    // Handle visibility change (for performance)
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }
  
  setupTouchEvents() {
    let touchStartY = 0;
    let touchPaused = false;
    
    this.addEventListener('touchstart', (e) => {
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    
    this.addEventListener('touchmove', (e) => {
      const touchCurrentY = e.touches[0].clientY;
      const touchDiff = Math.abs(touchCurrentY - touchStartY);
      
      // If user scrolls vertically, don't pause announcement
      if (touchDiff > 10) return;
      
      // For horizontal swipes or taps, pause animation
      if (!touchPaused) {
        this.content.style.animationPlayState = 'paused';
        touchPaused = true;
        
        // Resume after 3 seconds
        setTimeout(() => {
          if (!this.isHovered && !this.hasFocus && !document.hidden) {
            this.content.style.animationPlayState = 'running';
          }
          touchPaused = false;
        }, 3000);
      }
    }, { passive: true });
  }
  
  setupResizeObserver() {
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        // Debounce the animation recalculation
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          this.calculateAndSetAnimation();
        }, 150);
      });
      
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.content);
    } else {
      // Fallback for older browsers
      window.addEventListener('resize', this.debounce(() => {
        this.calculateAndSetAnimation();
      }, 150));
    }
  }
  
  calculateAndSetAnimation() {
    if (!this.content || this.messages.length === 0) return;
    
    // Get the total width of all original messages + gaps
    let totalWidth = 0;
    
    this.messages.forEach((message, index) => {
      const messageWidth = message.offsetWidth;
      totalWidth += messageWidth;
      
      // Add gap after each message except the last
      if (index < this.messages.length - 1) {
        totalWidth += this.messageGap;
      }
    });
    
    // Add one more gap to complete the loop
    totalWidth += this.messageGap;
    
    // Calculate animation duration based on scroll speed
    // Duration = distance / speed
    this.animationDuration = totalWidth / this.scrollSpeed;
    
    // Apply the animation duration
    this.content.style.animationDuration = `${this.animationDuration}s`;
    
    // Emit custom event for analytics or other scripts
    this.dispatchEvent(new CustomEvent('announcement-bar:calculated', {
      detail: {
        totalWidth,
        duration: this.animationDuration,
        speed: this.scrollSpeed
      }
    }));
  }
  
  handleMouseEnter() {
    this.isHovered = true;
    this.content.style.animationPlayState = 'paused';
    
    // Emit enhanced GTM event for hover analytics
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'announcement_bar_interaction',
        announcement_bar_action: 'hover_pause',
        announcement_bar_speed: this.scrollSpeed,
        announcement_bar_messages: this.messages.length,
        engagement_type: 'announcement_bar'
      });
    }
  }
  
  handleMouseLeave() {
    this.isHovered = false;
    if (!document.hidden && !this.hasFocus) {
      this.content.style.animationPlayState = 'running';
    }
  }
  
  handleFocusIn() {
    this.hasFocus = true;
    this.content.style.animationPlayState = 'paused';
  }
  
  handleFocusOut() {
    this.hasFocus = false;
    if (!this.isHovered && !document.hidden) {
      this.content.style.animationPlayState = 'running';
    }
  }
  
  handleVisibilityChange() {
    if (document.hidden) {
      this.content.style.animationPlayState = 'paused';
    } else if (!this.isHovered && !this.hasFocus) {
      this.content.style.animationPlayState = 'running';
    }
  }
  
  handleReducedMotion() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleMotionChange = (e) => {
      if (e.matches) {
        this.content.style.animation = 'none';
        this.content.style.justifyContent = 'center';
        
        // Hide duplicate messages for reduced motion
        const duplicates = this.querySelectorAll('.scrolling-announcement-bar__message--duplicate');
        duplicates.forEach(duplicate => {
          duplicate.style.display = 'none';
        });
      } else {
        this.calculateAndSetAnimation();
        
        // Show duplicate messages again
        const duplicates = this.querySelectorAll('.scrolling-announcement-bar__message--duplicate');
        duplicates.forEach(duplicate => {
          duplicate.style.display = '';
        });
      }
    };
    
    // Check on initialization
    handleMotionChange(reducedMotion);
    
    // Listen for changes
    reducedMotion.addEventListener('change', handleMotionChange);
  }
  
  // Update settings dynamically (for theme editor)
  updateSettings(newSettings) {
    if (newSettings.scrollSpeed !== undefined) {
      this.scrollSpeed = parseInt(newSettings.scrollSpeed);
    }
    if (newSettings.messageGap !== undefined) {
      this.messageGap = parseInt(newSettings.messageGap);
    }
    if (newSettings.messagePadding !== undefined) {
      this.messagePadding = parseInt(newSettings.messagePadding);
    }
    
    this.applySettings();
    this.calculateAndSetAnimation();
  }
  
  // Utility function for debouncing
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  // Cleanup
  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}

// Register the custom element
customElements.define('scrolling-announcement-bar', ScrollingAnnouncementBar);

// Lightweight initialization - removed as duplicated in custom element

// Enhanced error handling for custom elements
if (!window.customElements) {
  console.warn('Custom elements not supported. Announcement bar may not function properly.');
}

// Theme Editor Support - relies on custom element auto-initialization
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    // Custom elements auto-initialize, no manual intervention needed
  });
  
  document.addEventListener('shopify:section:unload', function(event) {
    // Custom elements auto-cleanup via disconnectedCallback
  });
  
  document.addEventListener('shopify:section:reorder', function(event) {
    // Custom elements auto-initialize, no manual intervention needed
  });
  
  document.addEventListener('shopify:block:select', function(event) {
    const announcementBar = event.target.closest('scrolling-announcement-bar');
    if (announcementBar) {
      const content = announcementBar.querySelector('.scrolling-announcement-bar__content');
      if (content) content.style.animationPlayState = 'paused';
    }
  });
  
  document.addEventListener('shopify:block:deselect', function(event) {
    const announcementBar = event.target.closest('scrolling-announcement-bar');
    if (announcementBar) {
      const content = announcementBar.querySelector('.scrolling-announcement-bar__content');
      if (content) content.style.animationPlayState = 'running';
    }
  });
}

// Performance optimization: Intersection Observer for pause when out of view
if (window.IntersectionObserver) {
  const observerOptions = {
    root: null,
    rootMargin: '50px',
    threshold: 0
  };
  
  const announcementObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const announcementBar = entry.target;
      const content = announcementBar.querySelector('.scrolling-announcement-bar__content');
      
      if (entry.isIntersecting) {
        // Resume animation when in view
        if (!announcementBar.isHovered && !announcementBar.hasFocus && !document.hidden) {
          content.style.animationPlayState = 'running';
        }
      } else {
        // Pause animation when out of view for performance
        content.style.animationPlayState = 'paused';
      }
    });
  }, observerOptions);
  
  // Observe all announcement bars - immediate execution
  const observeAnnouncementBars = () => {
    const announcementBars = document.querySelectorAll('scrolling-announcement-bar');
    announcementBars.forEach(bar => {
      announcementObserver.observe(bar);
    });
  };
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAnnouncementBars);
  } else {
    observeAnnouncementBars();
  }
} 